import { formatCurrency, formatNumber } from './components/SharedFrames';

export const toEpochMs = (value) => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date.getTime();
};

const computeCiLegOpenMtm = (leg) => {
    if (!leg || String(leg.status || 'active').toLowerCase() !== 'active') {
        return 0;
    }
    const unrealized = leg.unrealizedPnl ?? leg.unrealized_pnl;
    if (unrealized != null && Number.isFinite(Number(unrealized))) {
        return Number(unrealized);
    }
    const ltp = leg.currentLtp ?? leg.current_ltp ?? leg.ltp ?? leg.currentPrice ?? leg.lastPrice;
    const entry = leg.entryPrice ?? leg.entry_price ?? leg.avgPrice ?? leg.price;
    const qty = leg.qty ?? leg.quantity;
    if (ltp != null && entry != null && qty != null) {
        return (Number(ltp) - Number(entry)) * Number(qty);
    }
    return 0;
};

const sumCiLegOpenMtm = (layers) => (
    (layers || []).reduce((sum, layer) => {
        const layerLegs = Array.isArray(layer?.legs) ? layer.legs : [];
        return sum + layerLegs.reduce((legSum, leg) => legSum + computeCiLegOpenMtm(leg), 0);
    }, 0)
);

const roundPnl = (value) => Number(Number(value || 0).toFixed(2));

/**
 * Resolve Chanakya Inverse live P&L for display.
 *
 * Prefer journal-backed dayPnl + openPnl over a stale layer netPnl. When the
 * backend marks telemetry stale (or reported netPnl disagrees with
 * realized+open), recompute so Day P&L cannot show yesterday's closed session
 * total while Realized shows today's journal figure.
 */
export const resolveChanakyaInverseLivePnl = (summary, layers = []) => {
    const dayPnl = roundPnl(summary?.dayPnl);
    const openPnl = summary?.openPnl != null
        ? roundPnl(summary.openPnl)
        : roundPnl(sumCiLegOpenMtm(layers));
    const recomputed = roundPnl(dayPnl + openPnl);
    const reported = summary?.netPnl != null && Number.isFinite(Number(summary.netPnl))
        ? roundPnl(summary.netPnl)
        : null;
    const telemetryStale = summary?.pnlTelemetryStale === true
        || summary?.pnlSource === 'journal';
    const inconsistent = reported != null && Math.abs(reported - recomputed) > 0.05;
    const netPnl = (telemetryStale || inconsistent || reported == null)
        ? recomputed
        : reported;
    return { dayPnl, openPnl, netPnl };
};

export const buildChanakyaInverseStateMessage = (summary, layers = []) => {
    const phase = (summary?.phase || summary?.status || '').toString().toUpperCase();
    const openSlots = Array.isArray(summary?.openSlots) ? summary.openSlots : [];
    const rsi = summary?.indicators?.rsi;
    const { netPnl, dayPnl } = resolveChanakyaInverseLivePnl(summary, layers);

    const detail = [];
    if (rsi != null) {
        detail.push(`RSI ${formatNumber(rsi, 1)}`);
    }
    if (netPnl != null) {
        detail.push(`Day P&L ${formatCurrency(netPnl)}`);
    } else if (dayPnl != null) {
        detail.push(`Day P&L ${formatCurrency(dayPnl)}`);
    }
    const suffix = detail.length ? ` · ${detail.join(' · ')}` : '';

    if (openSlots.length) {
        return `Position active — ${openSlots.length} slot${openSlots.length > 1 ? 's' : ''} open${suffix}`;
    }

    switch (phase) {
        case 'WARMING_UP':
            return `Warming up — collecting bars${suffix}`;
        case 'HALTED':
            return `Halted — drawdown guard active${suffix}`;
        case 'CLOSED':
            return `Session closed${suffix}`;
        case 'ARMED':
        default:
            return `Armed — awaiting next entry${suffix}`;
    }
};

export const buildViduraStateMessage = (summary) => {
    // gate/positions/pnl arrive top-level on /state; defensively read from
    // summary if nested there.
    const gate = summary?.gate || {};
    const decision = (gate.lastDecision || '').toString().toUpperCase();
    const reason = (gate.lastReason || '').toString().trim();
    const detail = [];
    if (gate.ivRank != null && gate.ivThreshold != null) {
        detail.push(`IV-rank ${formatNumber(gate.ivRank, 2)} / ${formatNumber(gate.ivThreshold, 2)}`);
    }
    if (reason) {
        detail.push(reason);
    }
    const suffix = detail.length ? ` — ${detail.join(' · ')}` : '';

    switch (decision) {
        case 'DEPLOY':
            return `Gate DEPLOY${suffix}`;
        case 'SKIP':
            return `Gate SKIP${suffix}`;
        case 'MISSED':
            return `Gate MISSED${suffix}`;
        default:
            return `Lockbox idle — awaiting next gate decision${suffix}`;
    }
};

export const deriveStateEventMessage = (summary, hasOpenPosition = false, layers = []) => {
    const reasonCode = (summary?.reasonCode || '').toString();
    const lastMessage = (summary?.lastMessage || '').toString().trim();
    const staleMonitorError =
        lastMessage.startsWith('Monitor iteration failed:') &&
        (summary?.status || '').toString().toLowerCase() !== 'error';

    const mapped = {
        'warmup.collecting_bars': 'Collecting enough 5-minute bars for ADX/ATR.',
        'armed.waiting_entry_window': 'Armed and waiting for entry window.',
        'armed.sideways_regime': 'Sideways Regime Detected',
        'armed.cautious_regime': 'Cautious Regime',
        'armed.awaiting_signal_candle': 'Waiting for signal candle.',
        'armed.awaiting_entry_signal_time': 'Waiting for configured signal candle.',
        'waiting_reentry.awaiting_adx_recovery': 'Waiting for ADX recovery before re-entry.',
        'waiting_reentry.awaiting_signal_candle': 'ADX recovered, waiting for fresh signal candle.',
        'waiting_reentry.after_stop': 'SL hit. Waiting for ADX to recover above threshold...',
        'waiting_reentry.adx_recovered': 'ADX recovered. Re-evaluating entry.',
        'active.position_open': hasOpenPosition ? 'Position is active.' : 'Strategy is active.',
        'active.trailing_stop': 'Trailing stop is active.',
        'closed.session_complete': 'Session closed.',
        'halted.max_daily_flips': 'Max daily flips reached.',
        'halted.daily_loss_limit': 'Daily loss limit breached.',
    }[reasonCode];

    if (mapped) {
        return mapped;
    }
    if ((summary?.strategyId || '').toString().toLowerCase() === 'chanakya_inverse') {
        return buildChanakyaInverseStateMessage(summary, layers);
    }
    if ((summary?.strategyId || '').toString().toLowerCase() === 'vidura') {
        return buildViduraStateMessage(summary);
    }
    if (lastMessage && !staleMonitorError) {
        return lastMessage;
    }
    if (hasOpenPosition) {
        return 'Position is active.';
    }
    if ((summary?.status || '').toString().toLowerCase() === 'armed') {
        return 'Awaiting the next entry decision.';
    }
    return summary?.stateCommentary || 'Awaiting the next strategy update.';
};

export const toneForSeverity = (severity) => {
    const value = (severity || '').toString().toLowerCase();
    if (value === 'error' || value === 'critical') {
        return 'error';
    }
    if (value === 'warning' || value === 'warn') {
        return 'warning';
    }
    return 'primary';
};

export const buildEventFeedRows = ({
    summary = {},
    events = [],
    positions = [],
    latestLayer = null,
    latestActivity = null,
    layers = [],
    limit = 10,
}) => {
    const latestSummaryAt =
        summary.lastHeartbeatAt ||
        latestActivity?.capturedAt ||
        latestLayer?.capturedAt ||
        null;
    const latestSummaryAtMs = toEpochMs(latestSummaryAt);
    const latestEventAtMs = toEpochMs(events[0]?.occurredAt);
    const syntheticEventMessage = deriveStateEventMessage(summary, positions.length > 0, layers);
    const shouldInjectStateEvent =
        Boolean(syntheticEventMessage) &&
        Boolean(latestSummaryAtMs) &&
        (!latestEventAtMs || latestSummaryAtMs > latestEventAtMs || events[0]?.message !== syntheticEventMessage);

    if (shouldInjectStateEvent) {
        return [
            {
                occurredAt: latestSummaryAt,
                severity: summary.status === 'halted' ? 'warning' : 'info',
                message: syntheticEventMessage,
                meta: {
                    commentary: summary.stateCommentary,
                    phase: summary.status,
                    regime: summary.regime,
                    decision: positions.length ? 'hold' : 'wait',
                    adx: summary.adxValue,
                    atr: summary.atrValue,
                    reasonCode: summary.reasonCode,
                },
            },
            ...events.filter(
                (event) => event?.message !== syntheticEventMessage || event?.occurredAt !== latestSummaryAt
            ),
        ].slice(0, limit);
    }

    return events.slice(0, limit);
};
